import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { MessageCircleQuestion, Send, Loader2 } from "lucide-react";
import { apiPost } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
export function AgentQuestion({
  runId,
  stepId,
  question,
  options,
  answer,
  canAnswer,
  onAnswer,
}: {
  runId: string;
  stepId: string;
  question: string;
  options?: string[];
  answer?: string;
  canAnswer: boolean;
  onAnswer(): void;
}) {
  const fr = useI18n().language === "fr";
  const [draft, setDraft] = useState("");
  const locked = useRef(false);
  const [submitted, setSubmitted] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: (value: string) =>
      apiPost(`/ai/runs/${runId}/answer`, { stepId, answer: value }),
    onSuccess: (_result, value) => { setSubmitted(value); onAnswer(); },
    onError: () => { locked.current = false; },
  });
  const submit = (value: string) => {
    if (locked.current || answer || submitted || !value.trim()) return;
    locked.current = true;
    send.mutate(value);
  };
  return (
    <div className="mt-3 rounded-xl border border-primary/25 bg-primary/5 p-4 space-y-3">
      <p className="flex items-start gap-2 font-medium">
        <MessageCircleQuestion className="h-4 w-4 mt-0.5 text-primary shrink-0" />
        {question}
      </p>
      {answer || submitted ? (
        <p className="text-sm whitespace-pre-wrap text-foreground-secondary">{answer ?? submitted}</p>
      ) : (
        canAnswer && (
          <>
            <div className="flex flex-col gap-2">
              {options?.map((option) => (
                <Button
                  key={option}
                  variant="outline"
                  size="sm"
                  className="h-auto w-full justify-start px-3 py-3 whitespace-normal text-left leading-relaxed"
                  disabled={send.isPending}
                  onClick={() => submit(option)}
                >
                  {option}
                </Button>
              ))}
            </div>
            <form
              className="space-y-2 border-t border-primary/15 pt-3"
              onSubmit={(event) => {
                event.preventDefault();
                submit(draft);
              }}
            >
              <label htmlFor={`answer-${stepId}`} className="block text-sm font-medium">{fr ? "Votre réponse personnalisée" : "Your custom answer"}</label>
              <Textarea
                id={`answer-${stepId}`}
                rows={3}
                aria-label={fr ? "Votre réponse" : "Your answer"}
                placeholder={fr ? "Précisez votre réponse…" : "Your answer…"}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                maxLength={6000}
                disabled={send.isPending}
              />
              <Button
                className="ml-auto flex gap-2"
                type="submit"
                aria-label={fr ? "Répondre" : "Answer"}
                disabled={!draft.trim() || send.isPending}
              >
                {send.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {fr ? "Envoyer la réponse" : "Send answer"}
              </Button>
            </form>
          </>
        )
      )}
      {send.error && (
        <p role="alert" className="text-sm text-destructive">
          {send.error.message}
        </p>
      )}
    </div>
  );
}
